import React, { useState } from 'react';
import { useStripe, useElements, CardElement } from '@stripe/react-stripe-js';
import { Button, Alert } from 'reactstrap';
import { FaCreditCard } from 'react-icons/fa';
import axios from 'axios';
import * as user_urls from '../../urls/api-users/admin';
import { fetchMerchantSetupIntent, getCandidateAuth0Ids, getCurrentAuth0Id } from '../../services/paymentService';

const CARD_ELEMENT_OPTIONS = {
    style: {
        base: {
            color: '#32325d',
            fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
            fontSmoothing: 'antialiased',
            fontSize: '16px',
            '::placeholder': {
                color: '#aab7c4'
            }
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a'
        }
    }
};

// Inline styles for Stripe Element container
const stripeElementStyle = `
    .add-card-modal-wide .modal-dialog {
        max-width: 650px;
    }
    .add-card-stripe-element {
        height: 45px;
        padding: 10px 12px;
        width: 100%;
        color: #32325d;
        background-color: white;
        border: 1px solid #dee2e6;
        border-radius: 4px;
        box-shadow: 0 1px 3px 0 #e6ebf1;
        transition: box-shadow 150ms ease;
        box-sizing: border-box;
    }
    .add-card-stripe-element.StripeElement--focus {
        box-shadow: 0 1px 3px 0 #cfd7df;
        border-color: #80bdff;
    }
    .add-card-stripe-element.StripeElement--invalid {
        border-color: #fa755a;
    }
`;

const getSetupIntentSecret = payload => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }

    return payload.setup_intent || payload.client_secret || payload.clientSecret || null;
};

const getResponsePayload = response => {
    if (response && response.data && response.data.payload && typeof response.data.payload === 'object') {
        return response.data.payload;
    }

    return response && response.data && typeof response.data === 'object' ? response.data : null;
};

const getAuthHeaders = () => ({
    Authorization: 'Bearer ' + localStorage.getItem('access_token'),
    'content-type': 'application/json'
});

const AddCardForm = ({ onSuccess, merchantAuth0Id = null, merchantAuth0Ids = [], merchantProfileSeed = null, storeId = '', adminStoreProfileLookup = false, initialSetupIntentPayload = null, provisionMerchantProfile = true }) => {
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    const stripe = useStripe();
    const elements = useElements();

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!stripe || !elements) {
            return;
        }

        setSubmitting(true);
        setMessage({ type: '', text: '' });

        try {
            const auth0Ids = getCandidateAuth0Ids({ auth0Id: merchantAuth0Id, auth0Ids: merchantAuth0Ids });
            const auth0Id = merchantAuth0Id || auth0Ids[0] || getCurrentAuth0Id();
            const setupIntentAuth0Ids = auth0Id ? [auth0Id] : auth0Ids;

            if (!auth0Id) {
                throw new Error('Could not determine merchant account id');
            }

            let responsePayload = initialSetupIntentPayload;

            if (!getSetupIntentSecret(responsePayload)) {
                let response;
                let primarySetupIntentError = null;

                try {
                    response = {
                        data: await fetchMerchantSetupIntent({
                            auth0Id,
                            auth0Ids: setupIntentAuth0Ids,
                            merchantProfileSeed,
                            // Save-card setup intent should be merchant-scoped.
                            storeId: '',
                            preferUsersProxy: false,
                            provisionMerchantProfile,
                        })
                    };
                } catch (error) {
                    primarySetupIntentError = error;
                }

                if (!response) {
                    try {
                        response = await axios({
                            method: 'get',
                            headers: getAuthHeaders(),
                            url: user_urls.usersHost + '/payment/merchant-app-setup-intent'
                        });
                    } catch (fallbackError) {
                        throw primarySetupIntentError || fallbackError;
                    }
                }

                responsePayload = getResponsePayload(response);
            }

            const clientSecret = getSetupIntentSecret(responsePayload);

            if (!clientSecret) {
                throw new Error('Could not extract client secret from response');
            }

            // Confirm card setup with Stripe
            const result = await stripe.confirmCardSetup(clientSecret, {
                payment_method: {
                    card: elements.getElement(CardElement)
                }
            });

            if (result.error) {
                setMessage({ type: 'error', text: result.error.message });
                setSubmitting(false);
            } else {
                setMessage({ type: 'success', text: 'Card added successfully!' });
                elements.getElement(CardElement).clear();
                setSubmitting(false);

                // Call onSuccess after a short delay to show success message
                setTimeout(() => {
                    if (onSuccess) onSuccess();
                }, 1500);
            }
        } catch (error) {
            const errorMessage = (error && error.response && error.response.data && error.response.data.message) || error.message || 'Something went wrong. Please try again.';
            setMessage({ type: 'error', text: errorMessage });
            setSubmitting(false);
        }
    };

    return (
        <>
            <style>{stripeElementStyle}</style>
            <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ color: '#212529', fontSize: '14px', marginBottom: '10px', display: 'block', fontWeight: '600' }}>
                        <FaCreditCard style={{ marginRight: '8px', color: '#495057' }} />
                        Card Details
                    </label>
                    <CardElement options={CARD_ELEMENT_OPTIONS} className="add-card-stripe-element" />
                    <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '8px', display: 'block' }}>
                        Enter your card number, expiration date, and CVC
                    </small>
                </div>

                {message.text && (
                    <Alert color={message.type === 'success' ? 'success' : 'danger'} style={{ marginBottom: '12px', padding: '10px 14px', fontSize: '13px' }}>
                        {message.text}
                    </Alert>
                )}

                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                    <Button
                        type="submit"
                        color="primary"
                        disabled={!stripe || submitting}
                        style={{ minWidth: '120px', padding: '8px 16px', fontSize: '13px', fontWeight: '500' }}
                    >
                        {submitting ? 'Saving...' : 'Save Card'}
                    </Button>
                </div>
            </form>
        </>
    );
};

export default AddCardForm;
